import { useCallback, useEffect, useState } from "react";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";

export type AiUsage = {
  spentUsd: number;
  limitUsd: number | null;
  periodLabel: string;
};

export type DiskUsage = {
  usedGb: number;
  totalGb: number;
};

export type ClawMetrics = {
  ai: AiUsage | null;
  disk: DiskUsage | null;
};

export type MetricsStatus = "idle" | "loading" | "ready" | "error";

export type UseClawMetricsResult = {
  status: MetricsStatus;
  metrics: ClawMetrics | null;
  isRevalidating: boolean;
  // Forces a refetch outside the mount/focus cycle — used after mutations
  // that change the upstream numbers (e.g. an OpenRouter key swap).
  refresh: () => void;
};

// Cached ClawMetrics payloads are keyed per user + tenant; bump the cache
// version in dashboardCache.ts if this shape changes.
const metricsResource = (tenantId: string): string => `metrics:${tenantId}`;

export const useClawMetrics = (tenantId: string | null): UseClawMetricsResult => {
  const [metrics, setMetrics] = useState<ClawMetrics | null>(null);
  const [status, setStatus] = useState<MetricsStatus>("idle");
  const [isRevalidating, setIsRevalidating] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);

  const refresh = useCallback((): void => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (tenantId === null) {
      setMetrics(null);
      setStatus("idle");
      return;
    }

    // Seed from the tenant-scoped cache so an instance switch (or reload)
    // renders last-known numbers immediately; the key includes the tenant
    // so we never render numbers tagged against the wrong instance.
    const cached = readUserCache<ClawMetrics>(metricsResource(tenantId));
    setMetrics(cached);
    setStatus(cached !== null ? "ready" : "loading");
    setIsRevalidating(true);

    const tenantForRequest = tenantId;
    const controller = new AbortController();
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const res = await fetch(
          `/api/claws/${encodeURIComponent(tenantForRequest)}/metrics`,
          { credentials: "include", signal: controller.signal },
        );
        if (cancelled) return;
        if (!res.ok) {
          setStatus((prev) => (prev === "ready" ? "ready" : "error"));
          setIsRevalidating(false);
          return;
        }
        const body = (await res.json()) as ClawMetrics;
        if (cancelled) return;
        setMetrics(body);
        setStatus("ready");
        setIsRevalidating(false);
        writeUserCache(metricsResource(tenantForRequest), body);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setStatus((prev) => (prev === "ready" ? "ready" : "error"));
        setIsRevalidating(false);
      }
    };

    void load();

    const onFocus = (): void => {
      void load();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      controller.abort();
      window.removeEventListener("focus", onFocus);
    };
  }, [tenantId, reloadKey]);

  return { status, metrics, isRevalidating, refresh };
};
